import IconBellNotification from 'assets/icons/bell-notification.svg';
import Button from 'components/button-new';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import { StaticPDPProduct, Variant } from 'modules/product-details/types';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { gtm, selectElement } from 'tracking';

interface BuyButtonProps {
  className?: string;
  isCartAnimationOn: boolean;
  product: StaticPDPProduct;
  selectedVariant?: Variant;
  onAddToCart: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onBackInStockClick: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
  ) => void;
}

const TRACKING_NAME = 'PDP Add-to-Cart';

const BuyButton: React.FC<BuyButtonProps> = ({
  className,
  isCartAnimationOn,
  product,
  selectedVariant,
  onAddToCart,
  onBackInStockClick,
}) => {
  // Array of product IDs to be set directly as out of stock as requested by the shop management team, as they will never be in stock but are used for bundles.
  const soldOutProducts = [6969936445474];

  const [loading, setLoading] = useState(false);
  const addToCart: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      setLoading(true);

      await onAddToCart(event);
      gtm(
        selectElement({
          element: TRACKING_NAME,
        })
      );

      setLoading(false);
    },
    [onAddToCart]
  );

  return (
    <ImpressionTrackerNew element={TRACKING_NAME}>
      <div className={className}>
        {selectedVariant && selectedVariant.availableForSale ? (
          <Button
            data-test="add-to-cart-cta"
            disabled={loading || isCartAnimationOn}
            layout="contained"
            actionDone={isCartAnimationOn}
            size="large"
            loading={loading}
            onClick={addToCart}
            iconLeft={isCartAnimationOn}
          >
            {isCartAnimationOn ? (
              <FormattedMessage id="pdp:buy-panel:added-to-cart" />
            ) : (
              <>
                {product && product.tags.includes('Preorder') ? (
                  <FormattedMessage id="pdp:buy-panel:preorder" />
                ) : (
                  <FormattedMessage id="pdp:buy-panel:add-to-cart" />
                )}
              </>
            )}
          </Button>
        ) : soldOutProducts.includes(product.id) ? (
          <Button
            data-test="back-in-stock-cta"
            layout="contained"
            size="large"
            disabled
          >
            <FormattedMessage id="pdp:buy-panel:product-out-stock" />
          </Button>
        ) : (
          <Button
            data-test="back-in-stock-cta"
            layout="contained"
            size="large"
            onClick={(event) => {
              event.preventDefault();
              onBackInStockClick(event);
            }}
          >
            <FormattedMessage id="pdp:buy-panel:back-in-stock" />
            <IconBellNotification className="h-4 w-4" />
          </Button>
        )}
      </div>
    </ImpressionTrackerNew>
  );
};

export default BuyButton;
